/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createTextNode } from 'lexical';
import { $isVariableNode, VariableNode } from '../../nodes/VariableNode';
import { VariableMap } from '../../types';

interface Props {
  variableMap?: VariableMap;
}

const $replaceVariableNode = (node: VariableNode, variableMap: VariableMap): void => {
  const variableKey = node.getVariableKey();
  const replacementValue = variableMap[variableKey];

  if (replacementValue) {
    const textNode = $createTextNode(replacementValue);
    textNode.setFormat(node.getFormat());
    node.replace(textNode);
  }
};

export const VariableReplacementPlugin = ({ variableMap }: Props): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!variableMap) {
      return;
    }

    const removeTransform = editor.registerNodeTransform(VariableNode, (node) => {
      $replaceVariableNode(node, variableMap);
    });

    editor.update(() => {
      const root = $getRoot();
      const allNodes = root.getAllTextNodes();

      allNodes.forEach((node) => {
        if ($isVariableNode(node)) {
          $replaceVariableNode(node, variableMap);
        }
      });
    });

    return () => {
      removeTransform();
    };
  }, [editor, variableMap]);

  return null;
};
